import { useState, useEffect, useCallback } from 'react';
import { bffQuery, bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';
import { IRAN_OFFSET_MS } from '@parsina/framework/clinical/src/lib/jalali';

// ─────────────────────────────────────────────────────────────────────────────
// «ثبت نوبت» from the doctor app — by the clinic's grant.
//
// Every clinic this doctor may book at comes with its terms (`canBookDirectly`).
// The grant is the clinic's, per practitioner, and ON by default; when a clinic
// has withdrawn it, a booking is still accepted but lands `requested` —
// «در انتظار تأیید کلینیک» — for the desk to confirm in «درخواست‌های نوبت».
//
// The BFF decides the grant again at write time from the membership row; the
// list here only lets the form SAY which of the two will happen before the
// doctor presses the button. It is display, never the authority.
// ─────────────────────────────────────────────────────────────────────────────

const BOOKING_CLINICS_QUERY = `
  query MyBookingClinics {
    myBookingClinics { clinicId name isOwner canBookDirectly visitDurationMinutes readOnly }
  }
`;

const BOOK_APPOINTMENT = `
  mutation BookAppointment($input: DoctorBookingInput!) {
    bookAppointment(input: $input) {
      outcome
      appointment { id status dateTime clinicId }
    }
  }
`;

const FIRST_AVAILABLE_SLOT = `
  query FirstAvailableSlot($clinicId: ID!, $fromDateTime: String!, $scope: FirstSlotScope!, $durationMinutes: Int) {
    firstAvailableSlot(clinicId: $clinicId, fromDateTime: $fromDateTime, scope: $scope, durationMinutes: $durationMinutes) {
      dateTime
      isAvailable
    }
  }
`;

const ADD_PATIENT = `
  mutation QuickAddPatient($input: CreatePatientInput!) {
    addPatient(input: $input) { id name phone }
  }
`;

// Every record already on one mobile, judged against ONE clinic: that clinic's own
// patients by name, everyone else masked (initials, birth year, gender). A mobile is
// a household, so this is a list the doctor chooses from, never a single answer.
// The match facts (PM-0536) say whose number it is on that record, and whether it is
// the record's main one; both are null on a masked record.
export const LOOKUP_PATIENTS_BY_PHONE = `
  query LookupPatientsByPhone($phone: String!, $clinicId: ID!) {
    lookupPatientsByPhone(phone: $phone, clinicId: $clinicId) {
      phoneNormalized
      candidates {
        patientId displayName isLinkedToThisClinic fileNumber birthYear gender hasAppAccount
        matchedRelation matchedIsPrimary
      }
    }
  }
`;

const WITHDRAW_REQUEST = `
  mutation WithdrawAppointmentRequest($appointmentId: ID!) {
    withdrawAppointmentRequest(appointmentId: $appointmentId) { id status }
  }
`;

// «تغییر زمان نوبت» (PM-0102) — the caller's own `scheduled` appointment, moved.
const RESCHEDULE_APPOINTMENT = `
  mutation RescheduleAppointment($appointmentId: ID!, $newDateTime: String!) {
    rescheduleAppointment(appointmentId: $appointmentId, newDateTime: $newDateTime) { id status dateTime clinicId }
  }
`;

// «ویرایش نوبت» (PM-0531) — the other two things the dialog can change on the
// doctor's OWN appointment besides its time. Both mutations already exist on the
// BFF; the Visit room (kit) calls `setVisitMode` for the same conversion.
const SET_VISIT_MODE = `
  mutation SetVisitMode($patientId: ID!, $appointmentId: ID, $online: Boolean!) {
    setVisitMode(patientId: $patientId, appointmentId: $appointmentId, online: $online) {
      appointmentId canJoin canConvert reason
    }
  }
`;

const VIDEO_AVAILABILITY = `
  query VideoVisitAvailability($patientId: ID!, $appointmentId: ID) {
    videoVisitAvailability(patientId: $patientId, appointmentId: $appointmentId) {
      appointmentId canJoin canConvert reason
    }
  }
`;

const SET_APPOINTMENT_NOTE = `
  mutation SetAppointmentNote($appointmentId: ID!, $note: String!) {
    setAppointmentNote(appointmentId: $appointmentId, note: $note) { id reminderNote }
  }
`;

/** 'YYYY-MM-DD HH:MM' in Iran local time → epoch ms. null when it is not one. */
export function iranLocalToEpoch(value) {
  const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})$/.exec(String(value || '').trim());
  if (!m) return null;
  const ms = Date.parse(`${m[1]}T${m[2]}:${m[3]}:00Z`);
  return Number.isFinite(ms) ? ms - IRAN_OFFSET_MS : null;
}

/** Epoch ms → 'YYYY-MM-DD HH:MM' in Iran local time — the inverse of the above. '' when it is not an instant. */
export function epochToIranLocal(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '';
  const iso = new Date(n + IRAN_OFFSET_MS).toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)}`;
}

/** Epoch ms → the Iran-local 'YYYY-MM-DD' it falls on. null when it is not an instant. */
export function iranDayOf(ms) {
  const n = Number(ms);
  return Number.isFinite(n) && n > 0 ? new Date(n + IRAN_OFFSET_MS).toISOString().slice(0, 10) : null;
}

/**
 * «اولین نوبت» — a debounced preview of the slot an auto-pick booking would take
 * at this clinic. `slot` is null when nothing is free in the scope. Advisory: the
 * BFF resolves the slot again when it books, so a failed preview blocks nothing.
 */
export function useFirstSlot({ enabled, clinicId, fromDateTime, scope, durationMinutes }) {
  const [state, setState] = useState({ slot: null, loading: false, error: false });

  useEffect(() => {
    if (!enabled || !clinicId || !fromDateTime) {
      setState({ slot: null, loading: false, error: false });
      return undefined;
    }
    let alive = true;
    setState({ slot: null, loading: true, error: false });
    const timer = setTimeout(() => {
      bffQuery(FIRST_AVAILABLE_SLOT, { clinicId, fromDateTime, scope, durationMinutes: durationMinutes || null })
        .then((data) => { if (alive) setState({ slot: data?.firstAvailableSlot || null, loading: false, error: false }); })
        .catch(() => { if (alive) setState({ slot: null, loading: false, error: true }); });
    }, 300);
    return () => { alive = false; clearTimeout(timer); };
  }, [enabled, clinicId, fromDateTime, scope, durationMinutes]);

  return state;
}

/** The clinics this doctor may book at. `clinics` is null until the first answer. */
export function useBookingClinics({ enabled = true } = {}) {
  const [clinics, setClinics] = useState(null);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    setError(null);
    bffQuery(BOOKING_CLINICS_QUERY)
      .then((data) => { if (alive) setClinics(data?.myBookingClinics || []); })
      .catch((err) => {
        if (!alive) return;
        setError(err);
        setClinics((prev) => prev || []);
      });
    return () => { alive = false; };
  }, [enabled, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { clinics, loading: enabled && clinics === null, error, reload };
}

export async function bookAppointment(input) {
  const data = await bffMutation(BOOK_APPOINTMENT, { input });
  return data?.bookAppointment || null;
}

// `allowSharedPhone` is the doctor's answer to «این شماره پرونده‌های دیگری دارد»: yes,
// and this is a different person. Without it a number that already carries records is
// refused with PHONE_HAS_PATIENTS.
export async function quickAddPatient({ name, phone, nationalId, allowSharedPhone = false }) {
  const data = await bffMutation(ADD_PATIENT, {
    input: {
      name: name.trim(), phone: phone || null, nationalId: nationalId || null,
      ...(allowSharedPhone ? { allowSharedPhone: true } : {}),
    },
  });
  return data?.addPatient || null;
}

export async function rescheduleAppointment(appointmentId, newDateTimeMs) {
  const data = await bffMutation(RESCHEDULE_APPOINTMENT, {
    appointmentId, newDateTime: String(newDateTimeMs),
  });
  return data?.rescheduleAppointment || null;
}

/** «تبدیل به ویزیت آنلاین» and back — the TYPE of the appointment, nothing else (its price stays). */
export async function setVisitMode(patientId, appointmentId, online) {
  const data = await bffMutation(SET_VISIT_MODE, { patientId, appointmentId, online });
  return data?.setVisitMode || null;
}

/** Can this appointment be made online here? `reason` is `not_configured` when the deployment has no media server. */
export async function fetchVideoAvailability(patientId, appointmentId) {
  const data = await bffQuery(VIDEO_AVAILABILITY, { patientId, appointmentId });
  return data?.videoVisitAvailability || null;
}

export async function setAppointmentNote(appointmentId, note) {
  const data = await bffMutation(SET_APPOINTMENT_NOTE, { appointmentId, note });
  return data?.setAppointmentNote || null;
}

export async function withdrawAppointmentRequest(appointmentId) {
  const data = await bffMutation(WITHDRAW_REQUEST, { appointmentId });
  return data?.withdrawAppointmentRequest || null;
}

/** The GraphQL error's stable code, whichever shape bffClient surfaced it in. */
export function errorCode(err) {
  return err?.extensions?.code
    || err?.graphQLErrors?.[0]?.extensions?.code
    || err?.errors?.[0]?.extensions?.code
    || null;
}
